import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import type { UseFormSetValue } from "react-hook-form";

import { orpc } from "@/client";
import { DocumentosLabels } from "@/lib/labels/documentos";
import {
	SignaturePreparation,
	type SignaturePreparationForm,
} from "@/routes/_app/pessoa/-utils/signature-preparation";
import type { SignatureAdjustment, SignatureRequirement, SignatureTerm } from "@/types/signatures";

import {
	SendTermsAssistantComposer,
	SendTermsAssistantConversation,
	SendTermsAssistantProposal,
} from "./assistant-ui";

export function SendTermsAssistant({
	personId,
	terms,
	requirements,
	customValues,
	patternValues,
	setValue,
	disabled,
}: {
	personId: number;
	terms: SignatureTerm[];
	requirements: SignatureRequirement[];
	customValues: SignaturePreparationForm["customValues"];
	patternValues: SignaturePreparationForm["patternValues"];
	setValue: UseFormSetValue<SignaturePreparationForm>;
	disabled: boolean;
}) {
	const [message, setMessage] = useState("");
	const [history, setHistory] = useState<
		{ id: string; role: "user" | "assistant"; text: string }[]
	>([]);
	const [conversation, setConversation] = useState<
		{ role: "user" | "assistant"; content: string }[]
	>([]);
	const [proposal, setProposal] = useState<SignatureAdjustment>();
	const { mutate, isPending } = useMutation(
		orpc.signatures.adjustPreparation.mutationOptions({
			onSuccess: (result) => {
				setConversation((current) => [...current, { role: "assistant", content: result.reply }]);
				setHistory((current) => [
					...current,
					{
						id: crypto.randomUUID(),
						role: "assistant",
						text:
							result.changes.length === 0
								? result.reply
								: DocumentosLabels.preparation.adjustmentsProposed(result.changes.length),
					},
				]);
				setProposal(result.changes.length === 0 ? undefined : result);
			},
			onError: () => {
				setConversation((current) => current.slice(0, -1));
				setHistory((current) => [
					...current,
					{
						id: crypto.randomUUID(),
						role: "assistant",
						text: DocumentosLabels.preparation.assistantUnavailable,
					},
				]);
			},
		}),
	);

	const handleSubmit = () => {
		const text = message.trim();

		if (text === "" || isPending || terms.length === 0) {
			return;
		}

		const messages = [...conversation.slice(-18), { role: "user" as const, content: text }];

		setConversation(messages);
		setHistory((current) => [...current, { id: crypto.randomUUID(), role: "user", text }]);
		setProposal(undefined);
		setMessage("");

		mutate({
			personId,
			termIds: terms.map((term) => term.id),
			messages,
			terms: terms.map((term) => {
				const requirement = requirements.find((candidate) => candidate.termId === term.id);

				return {
					termId: term.id,
					fields: (requirement?.fields ?? []).map((field) => ({
						fieldId: field.fieldId,
						value: customValues[SignaturePreparation.fieldKey(term.id, field.fieldId)] ?? "",
					})),
					patternItems: (requirement?.patterns ?? []).flatMap((pattern) =>
						SignaturePreparation.items(
							patternValues,
							SignaturePreparation.patternKey(term.id, pattern.patternId),
						).map((item) => ({
							patternId: pattern.patternId,
							itemId: item.itemId,
							fields: pattern.fields.map((field) => ({
								fieldId: field.fieldId,
								value: item.values[String(field.fieldId)] ?? "",
							})),
						})),
					),
				};
			}),
		});
	};

	const handleApply = () => {
		if (proposal === undefined) {
			return;
		}

		const stale = proposal.changes.some((change) => {
			if (change.itemId === null) {
				return (
					(customValues[SignaturePreparation.fieldKey(change.termId, change.fieldId)] ?? "") !==
					change.before
				);
			}

			const requirement = requirements.find((candidate) => candidate.termId === change.termId);
			const pattern = requirement?.patterns.find((candidate) =>
				candidate.fields.some((field) => field.fieldId === change.fieldId),
			);

			if (pattern === undefined) {
				return true;
			}

			const item = SignaturePreparation.items(
				patternValues,
				SignaturePreparation.patternKey(change.termId, pattern.patternId),
			).find((candidate) => candidate.itemId === change.itemId);

			return (item?.values[String(change.fieldId)] ?? "") !== change.before;
		});

		if (stale) {
			setHistory((current) => [
				...current,
				{
					id: crypto.randomUUID(),
					role: "assistant",
					text: DocumentosLabels.preparation.adjustmentsStale,
				},
			]);
			setProposal(undefined);

			return;
		}

		for (const change of proposal.changes) {
			if (change.itemId === null) {
				setValue(
					`customValues.${SignaturePreparation.fieldKey(change.termId, change.fieldId)}`,
					change.after,
					{ shouldDirty: true, shouldValidate: true },
				);

				continue;
			}

			const requirement = requirements.find((candidate) => candidate.termId === change.termId);
			const pattern = requirement?.patterns.find((candidate) =>
				candidate.fields.some((field) => field.fieldId === change.fieldId),
			);

			if (pattern === undefined) {
				throw new Error(`signature_assistant_pattern_missing:${change.fieldId}`);
			}

			const key = SignaturePreparation.patternKey(change.termId, pattern.patternId);
			const index = SignaturePreparation.items(patternValues, key).findIndex(
				(item) => item.itemId === change.itemId,
			);

			if (index === -1) {
				throw new Error(`signature_assistant_item_missing:${change.itemId}`);
			}

			setValue(`patternValues.${key}.${index}.values.${change.fieldId}`, change.after, {
				shouldDirty: true,
				shouldValidate: true,
			});
		}

		setHistory((current) => [
			...current,
			{
				id: crypto.randomUUID(),
				role: "assistant",
				text: DocumentosLabels.preparation.adjustmentsApplied(proposal.changes.length),
			},
		]);
		setProposal(undefined);
	};

	return (
		<div
			data-component="signature-assistant"
			data-turns={conversation.length}
			data-pending={isPending}
			className="contents"
		>
			<SendTermsAssistantConversation
				termsSelected={terms.length > 0}
				history={history}
				isPending={isPending}
			/>

			{proposal !== undefined && (
				<SendTermsAssistantProposal
					proposal={proposal}
					onDiscard={() => setProposal(undefined)}
					onApply={handleApply}
				/>
			)}

			<SendTermsAssistantComposer
				message={message}
				disabled={disabled || isPending || terms.length === 0}
				onMessageChange={setMessage}
				onSubmit={handleSubmit}
			/>
		</div>
	);
}
